import React, { useMemo } from 'react';

/**
 * Queue Performance Chart Component
 * Displays historical performance data in various chart formats
 */
const QueuePerformanceChart = ({ 
  title, 
  data = [], 
  theme, 
  type = 'line',
  height = 300,
  showGrid = true,
  showTooltip = true 
}) => {
  // Process data for chart rendering
  const chartData = useMemo(() => {
    if (!data || data.length === 0) {
      return {
        points: [],
        maxValue: 0,
        minValue: 0,
        labels: []
      };
    }

    const values = data.map(d => d.value || 0);
    const maxValue = Math.max(...values);
    const minValue = Math.min(...values);
    const range = maxValue - minValue || 1;

    const points = data.map((d, index) => ({
      x: (index / (data.length - 1)) * 100,
      y: ((maxValue - (d.value || 0)) / range) * 80 + 10, // 10% padding top/bottom
      value: d.value || 0,
      timestamp: d.timestamp,
      label: d.label
    }));

    return {
      points,
      maxValue,
      minValue,
      labels: data.map(d => d.label || new Date(d.timestamp).toLocaleTimeString())
    };
  }, [data]);

  // Generate SVG path for line chart
  const generatePath = () => {
    if (chartData.points.length === 0) return '';
    
    if (type === 'area') {
      const linePath = chartData.points
        .map((point, index) => `${index === 0 ? 'M' : 'L'} ${point.x} ${point.y}`)
        .join(' ');
      
      // Close the area path
      const firstPoint = chartData.points[0];
      const lastPoint = chartData.points[chartData.points.length - 1];
      return `${linePath} L ${lastPoint.x} 90 L ${firstPoint.x} 90 Z`;
    }
    
    return chartData.points
      .map((point, index) => `${index === 0 ? 'M' : 'L'} ${point.x} ${point.y}`)
      .join(' ');
  };

  // Generate grid lines
  const generateGridLines = () => {
    if (!showGrid) return null;
    
    const lines = [];
    
    // Horizontal grid lines
    for (let i = 0; i <= 4; i++) {
      const y = 10 + (i * 20);
      lines.push(
        <line
          key={`h-${i}`}
          x1="0"
          y1={y}
          x2="100"
          y2={y}
          stroke={theme === 'dark' ? '#374151' : '#E5E7EB'}
          strokeWidth="0.5"
          strokeDasharray="2,2"
        />
      );
    }
    
    // Vertical grid lines
    for (let i = 0; i <= 4; i++) {
      const x = i * 25;
      lines.push(
        <line
          key={`v-${i}`}
          x1={x}
          y1="10"
          x2={x}
          y2="90"
          stroke={theme === 'dark' ? '#374151' : '#E5E7EB'}
          strokeWidth="0.5"
          strokeDasharray="2,2"
        />
      );
    }
    
    return lines;
  };

  const [hoveredPoint, setHoveredPoint] = React.useState(null);

  if (chartData.points.length === 0) {
    return (
      <div className={`${
        theme === 'dark' 
          ? 'bg-gray-800 border-gray-700' 
          : 'bg-white border-gray-200'
      } rounded-lg shadow border p-6`} style={{ height }}>
        <h3 className={`text-lg font-semibold mb-4 ${
          theme === 'dark' ? 'text-white' : 'text-gray-900'
        }`}>
          {title}
        </h3>
        <div className="flex items-center justify-center h-full">
          <div className="text-center">
            <div className={`text-4xl mb-2 ${
              theme === 'dark' ? 'text-gray-600' : 'text-gray-400'
            }`}>
              📊
            </div>
            <p className={`text-sm ${
              theme === 'dark' ? 'text-gray-400' : 'text-gray-600'
            }`}>
              No data available
            </p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className={`${
      theme === 'dark' 
        ? 'bg-gray-800 border-gray-700' 
        : 'bg-white border-gray-200'
    } rounded-lg shadow border p-6`} style={{ height }}>
      <div className="flex items-center justify-between mb-4">
        <h3 className={`text-lg font-semibold ${
          theme === 'dark' ? 'text-white' : 'text-gray-900'
        }`}>
          {title}
        </h3>
        
        <div className="flex items-center space-x-4">
          <div className={`text-sm ${
            theme === 'dark' ? 'text-gray-400' : 'text-gray-600'
          }`}>
            Max: {chartData.maxValue.toLocaleString()}
          </div>
          <div className={`text-sm ${
            theme === 'dark' ? 'text-gray-400' : 'text-gray-600'
          }`}>
            Min: {chartData.minValue.toLocaleString()}
          </div>
        </div>
      </div>
      
      <div className="relative" style={{ height: height - 100 }}>
        <svg
          width="100%"
          height="100%"
          viewBox="0 0 100 100"
          preserveAspectRatio="none"
          className="overflow-visible"
        >
          {/* Grid lines */}
          {generateGridLines()}
          
          {/* Chart area/line */}
          <path
            d={generatePath()}
            fill={type === 'area' ? 'rgba(20, 184, 166, 0.1)' : 'none'}
            stroke="#14B8A6"
            strokeWidth="0.5"
            strokeLinejoin="round"
            strokeLinecap="round"
          />
          
          {/* Data points */}
          {type === 'line' && chartData.points.map((point, index) => (
            <circle
              key={index}
              cx={point.x}
              cy={point.y}
              r="0.8"
              fill="#14B8A6"
              stroke="#ffffff"
              strokeWidth="0.3"
              className="cursor-pointer hover:r-1.2 transition-all"
              onMouseEnter={() => showTooltip && setHoveredPoint(point)}
              onMouseLeave={() => setHoveredPoint(null)}
            />
          ))}
        </svg>
        
        {/* Tooltip */}
        {showTooltip && hoveredPoint && (
          <div
            className={`absolute z-10 px-3 py-2 rounded-lg shadow-lg border ${
              theme === 'dark'
                ? 'bg-gray-700 border-gray-600 text-white'
                : 'bg-white border-gray-200 text-gray-900'
            }`}
            style={{
              left: `${hoveredPoint.x}%`,
              top: `${hoveredPoint.y}%`,
              transform: 'translate(-50%, -100%)',
              marginTop: '-8px'
            }}
          >
            <div className="text-sm font-medium">
              {hoveredPoint.value.toLocaleString()}
            </div>
            <div className={`text-xs ${
              theme === 'dark' ? 'text-gray-300' : 'text-gray-600'
            }`}>
              {hoveredPoint.label}
            </div>
          </div>
        )}
      </div>
      
      {/* X-axis labels */}
      <div className="flex justify-between mt-2">
        {chartData.labels.filter((_, index) => index % Math.ceil(chartData.labels.length / 5) === 0).map((label, index) => (
          <div
            key={index}
            className={`text-xs ${
              theme === 'dark' ? 'text-gray-400' : 'text-gray-600'
            }`}
          >
            {label}
          </div>
        ))}
      </div>
    </div>
  );
};

export default QueuePerformanceChart;
